import React, { useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { LogOut, Sparkles, Home } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import PartnerPortalSection from '../components/PartnerPortalSection';

// Standalone /partner route — most partners now access the portal through
// /dashboard (which embeds the same section as a tab), but this page is kept
// for direct deep-link compatibility and Square's post-checkout redirect.
export default function PartnerDashboard() {
  const navigate = useNavigate();
  const { user, token, logout, loading: authLoading } = useAuth();
  const { language, toggleLanguage } = useLanguage();

  useEffect(() => {
    if (authLoading) return;
    if (!user) { navigate('/login'); return; }
    if (user.role !== 'partner') {
      if (user.role === 'admin' || user.role === 'sub_admin') navigate('/admin');
      else navigate('/dashboard');
    }
  }, [user, authLoading, navigate]);

  if (authLoading || !user) return null;

  const handleLogout = () => { logout(); navigate('/login'); };

  return (
    <div className="min-h-screen bg-[#F6F7F9]" data-testid="partner-dashboard">
      <header className="bg-[#0B0E13] text-white">
        <div className="max-w-[1400px] mx-auto px-5 py-3 flex items-center justify-between gap-4">
          <Link to="/" className="flex items-center gap-2 min-w-0">
            <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-[#D4AF37] to-amber-600 flex items-center justify-center shrink-0 shadow-lg shadow-amber-500/30">
              <Sparkles className="w-4 h-4 text-white" strokeWidth={2.5} />
            </div>
            <div className="min-w-0">
              <p className="text-[10px] text-amber-400 font-bold tracking-wider">PARTNER</p>
              <p className="text-sm font-extrabold leading-tight truncate">TALITHA KOUMI TUG</p>
            </div>
          </Link>
          <div className="flex items-center gap-3">
            {/* Quick switch back to the user portal — keeps both portals one click away. */}
            <button
              data-testid="partner-goto-userdash-btn"
              onClick={() => navigate('/dashboard')}
              className="flex items-center gap-1.5 text-xs font-bold text-amber-300 hover:text-amber-200 px-3 py-1.5 rounded-md border border-amber-400/30 hover:border-amber-300/60 transition"
              title={language === 'fr' ? 'Aller au tableau de bord utilisateur' : 'Go to user dashboard'}
            >
              <Home className="w-3.5 h-3.5" />
              {language === 'fr' ? 'Mon Conférence' : 'My Conference'}
            </button>
            <button
              data-testid="partner-lang-toggle"
              onClick={toggleLanguage}
              className="text-xs font-bold text-gray-300 hover:text-white px-3 py-1.5 rounded-md border border-white/10 hover:border-white/30 transition"
            >
              {language === 'fr' ? 'EN' : 'FR'}
            </button>
            <button
              data-testid="partner-logout-btn"
              onClick={handleLogout}
              className="flex items-center gap-1.5 text-xs font-bold text-gray-300 hover:text-red-300 px-3 py-1.5 rounded-md border border-white/10 hover:border-red-400/30 transition"
            >
              <LogOut className="w-3.5 h-3.5" /> {language === 'fr' ? 'Déconnexion' : 'Sign Out'}
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-[1400px] mx-auto p-5">
        <PartnerPortalSection user={user} token={token} language={language} />
      </main>
    </div>
  );
}
